import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { statusStyle } from "@/lib/clients";
import { Users, UserPlus, UserCheck, Activity, MessageSquare, Paperclip, ArrowUpRight, DollarSign, Scale, AlertTriangle } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import DashboardCalendar from "@/components/DashboardCalendar";
import DashboardIntakes from "@/components/DashboardIntakes";
import DashboardCalls from "@/components/DashboardCalls";

const COLORS = ["#002FA7", "#2563eb", "#3b82f6", "#60a5fa", "#93c5fd", "#1e40af", "#1d4ed8", "#0ea5e9"];
const CHART_MARGIN = { top: 5, right: 10, left: -20, bottom: 0 };
const AXIS_TICK = { fontSize: 12, fill: "#6B7280" };
const X_AXIS_LINE = { stroke: "#E5E7EB" };
const TOOLTIP_CURSOR = { fill: "#F3F4F6" };
const TOOLTIP_STYLE = { borderRadius: 2, border: "1px solid #E5E7EB", fontSize: 13 };
const BAR_RADIUS = [2, 2, 0, 0];

function Kpi({ icon: Icon, label, value, hint, onClick, testid }) {
  return (
    <div onClick={onClick} data-testid={testid}
      className={`bg-white border border-gray-200 rounded-sm p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${onClick ? "cursor-pointer hover:border-[#002FA7]" : ""}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">{label}</span>
        <Icon className="w-[18px] h-[18px] text-[#002FA7]" strokeWidth={1.8} />
      </div>
      <p className="font-heading text-4xl font-extrabold tracking-tighter text-gray-900 mt-3">{value}</p>
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [myCount, setMyCount] = useState(null);
  const [costStats, setCostStats] = useState(null);
  const [solAlerts, setSolAlerts] = useState({ alerts: [], count: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/clients").then((r) => setRecent(r.data.slice(0, 5)));
    api.get("/clients", { params: { mine: true } }).then((r) => setMyCount(r.data.length)).catch(() => setMyCount(0));
    api.get("/costs/stats").then((r) => setCostStats(r.data)).catch(() => setCostStats(null));
    api.get("/docket/sol-alerts").then((r) => setSolAlerts(r.data)).catch(() => {});
  }, []);

  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  const statusData = stats ? [
    { name: "Lead", value: stats.by_status.lead },
    { name: "Active", value: stats.by_status.active },
    { name: "Inactive", value: stats.by_status.inactive },
  ] : [];

  return (
    <div className="p-6 lg:p-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between mb-7">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter text-gray-900">Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">Overview of your client database.</p>
        </div>
        <button onClick={() => navigate("/clients")} data-testid="view-clients-button"
          className="hidden sm:flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm transition-colors">
          View clients <ArrowUpRight className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-6">
        <Kpi icon={UserCheck} label="My clients" value={myCount ?? "—"} hint="Assigned to me" testid="kpi-my-clients"
          onClick={() => navigate("/clients?mine=1")} />
        <Kpi icon={Users} label="Total clients" value={stats?.total ?? "—"} />
        <Kpi icon={Activity} label="Active" value={stats?.by_status?.active ?? "—"} hint="Currently engaged" />
        <Kpi icon={UserPlus} label="New this week" value={stats?.new_this_week ?? "—"} hint="Last 7 days" />
        <Kpi icon={MessageSquare} label="Notes logged" value={stats?.notes_count ?? "—"} />
        <Kpi icon={Paperclip} label="Files" value={stats?.files_count ?? "—"} hint="Documents stored" />
        <Kpi icon={DollarSign} label="Total costs" value={costStats ? money(costStats.total_amount) : "—"}
          hint={costStats ? `${money(costStats.unpaid_amount)} unpaid` : "Case disbursements"} testid="kpi-total-costs" />
      </div>

      {solAlerts.count > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-sm p-5 mt-6" data-testid="dashboard-sol-alerts">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-lg font-bold text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Statute of limitations — {solAlerts.count} within 90 days
            </h3>
            <button onClick={() => navigate("/docket")} data-testid="dashboard-sol-open" className="text-xs font-semibold text-red-700 hover:underline">View docket</button>
          </div>
          <div className="space-y-1.5">
            {solAlerts.alerts.slice(0, 5).map((a) => (
              <button key={a.id} onClick={() => navigate(`/clients/${a.client_id}`)} data-testid={`dashboard-sol-${a.id}`}
                className="w-full flex items-center justify-between text-sm py-1 hover:bg-red-100/50 rounded-sm px-1 transition-colors text-left">
                <span className="text-gray-700 truncate"><Scale className="w-3.5 h-3.5 inline text-red-500 mr-1" />{a.client_name} — {a.title}</span>
                <span className={`font-bold shrink-0 ml-3 ${a.days_left <= 30 ? "text-red-600" : "text-amber-600"}`}>{a.sol_date.slice(0, 10)} · {a.days_left}d</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {costStats && costStats.count > 0 && (
        <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6 mt-6" data-testid="cost-payment-status">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#002FA7]" /> Costs by payment status
            </h3>
            <button onClick={() => navigate("/reports")} className="text-xs font-semibold text-[#002FA7] hover:underline">Cost report</button>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[
              { key: "unpaid", label: "Unpaid", value: costStats.unpaid_amount, color: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-50" },
              { key: "paid", label: "Paid", value: costStats.paid_amount, color: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50" },
              { key: "reimbursed", label: "Reimbursed", value: costStats.reimbursed_amount, color: "bg-blue-500", text: "text-blue-700", bg: "bg-blue-50" },
            ].map((s) => {
              const pct = costStats.total_amount ? Math.round((s.value / costStats.total_amount) * 100) : 0;
              return (
                <div key={s.key} className={`${s.bg} rounded-sm p-4`} data-testid={`payment-stat-${s.key}`}>
                  <p className={`font-heading text-2xl font-extrabold ${s.text}`}>{money(s.value)}</p>
                  <p className="text-xs font-semibold text-gray-500 mt-0.5 mb-2">{s.label} · {pct}%</p>
                  <div className="bg-white/70 rounded-sm h-2 overflow-hidden">
                    <div className={`h-full ${s.color}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <DashboardCalendar />

        <div className="bg-white border border-gray-200 rounded-sm p-6 shadow-sm">
          <h3 className="font-heading text-lg font-bold text-gray-900 mb-5">Clients by status</h3>
          <div className="h-64" data-testid="status-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusData} margin={CHART_MARGIN}>
                <XAxis dataKey="name" tick={AXIS_TICK} axisLine={X_AXIS_LINE} tickLine={false} />
                <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={TOOLTIP_CURSOR} contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="value" radius={BAR_RADIUS} maxBarSize={64}>
                  {statusData.map((d, i) => <Cell key={d.name} fill={COLORS[i]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-sm p-6 shadow-sm">
          <h3 className="font-heading text-lg font-bold text-gray-900 mb-5">Top tags</h3>
          {stats?.tag_distribution?.length ? (
            <div className="space-y-3" data-testid="tag-distribution">
              {stats.tag_distribution.map((t) => (
                <div key={t.tag} className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-700 w-28 truncate">{t.tag}</span>
                  <div className="flex-1 bg-gray-100 rounded-sm h-2.5 overflow-hidden">
                    <div className="h-full bg-[#002FA7]" style={{ width: `${(t.count / stats.tag_distribution[0].count) * 100}%` }} />
                  </div>
                  <span className="text-sm font-bold text-gray-900 w-6 text-right">{t.count}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-gray-400">No tags yet. Add tags to your clients.</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <DashboardIntakes />
        <DashboardCalls />

        <div className="bg-white border border-gray-200 rounded-sm shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="font-heading text-lg font-bold text-gray-900">Recent clients</h3>
          </div>
          {recent.length ? recent.map((c) => (
            <button key={c.id} onClick={() => navigate(`/clients/${c.id}`)} data-testid={`recent-client-${c.id}`}
              className="w-full flex items-center gap-4 px-6 py-3.5 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors text-left">
              <div className="w-9 h-9 rounded-full bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center font-bold text-sm shrink-0">
                {c.name?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{c.name}</p>
                <p className="text-xs text-gray-500 truncate">{c.company || c.email || "—"}</p>
              </div>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-sm border capitalize ${statusStyle(c.status)}`}>{c.status}</span>
            </button>
          )) : <p className="px-6 py-8 text-sm text-gray-400 text-center">No clients yet.</p>}
        </div>
      </div>
    </div>
  );
}
